import {
  Badge,
  Button,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import type { IconProps } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ComponentType } from "react";
import { PhoneInput } from "../../components/PhoneInput";
import { DataTableShell, PageHeader, SortableTh, StatusBadge, useTableSort, useVisibleWindow } from "../../components/ui";
import { companyApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { toPhoneForApi, validateIndianPhoneFieldOptional } from "../../lib/phone";
import type { CompanyRole } from "../../types/api";

const ROLE_OPTIONS: { value: CompanyRole; label: string }[] = [
  { value: "hr", label: "HR" },
  { value: "billing", label: "Billing" },
];

interface HrTeamPageProps {
  title?: string;
  subtitle?: string;
  icon?: ComponentType<IconProps>;
}

export function HrTeamPage({
  title = NAV_COPY.hr.team,
  subtitle = "People who can post jobs and review candidates for your company.",
  icon = NAV_ICONS.team,
}: HrTeamPageProps) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const companyQuery = useQuery({ queryKey: ["my-company"], queryFn: companyApi.myCompany });
  const companyId = companyQuery.data?.company_id;
  const isAdmin = companyQuery.data?.company_role === "company_admin";

  const usersQuery = useQuery({
    queryKey: ["company-users", companyId],
    queryFn: () => companyApi.listUsers(companyId!),
    enabled: !!companyId,
  });

  const form = useForm({
    initialValues: {
      full_name: "",
      email: "",
      password: "",
      phone: "",
      company_role: "hr" as CompanyRole,
    },
    validate: {
      full_name: (v) => (v.trim().length >= 2 ? null : "Required"),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Valid email required"),
      password: (v) => (v.length >= 8 ? null : "Min 8 characters"),
      phone: validateIndianPhoneFieldOptional,
    },
  });

  const addMutation = useMutation({
    mutationFn: (values: typeof form.values) =>
      companyApi.addUser(companyId!, {
        full_name: values.full_name,
        email: values.email,
        password: values.password,
        phone: values.phone ? toPhoneForApi(values.phone) : undefined,
        company_role: values.company_role,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", title: "User added", message: "Team member created." });
      queryClient.invalidateQueries({ queryKey: ["company-users", companyId] });
      form.reset();
      setAddOpen(false);
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Failed",
        message: err instanceof ApiError ? err.message : "Something went wrong",
      });
    },
  });

  const users = usersQuery.data ?? [];
  const { sorted, sortKey, direction, toggle } = useTableSort(users, {
    name: (r) => r.user.full_name,
    email: (r) => r.user.email,
    role: (r) => r.company_role,
    status: (r) => (r.is_active ? "active" : "inactive"),
  });
  const teamWindow = useVisibleWindow(sorted);

  return (
    <Stack gap="lg">
      <PageHeader
        icon={icon}
        title={title}
        subtitle={subtitle}
        actions={
          isAdmin ? (
            <Button color="violet" onClick={() => setAddOpen(true)}>
              Add user
            </Button>
          ) : undefined
        }
      />

      {!isAdmin && (
        <Text size="sm" c="dimmed">
          Only company admins can add users. You can view the team list.
        </Text>
      )}

      <DataTableShell
        loading={companyQuery.isLoading || usersQuery.isLoading}
        error={
          usersQuery.isError
            ? usersQuery.error instanceof Error
              ? usersQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void usersQuery.refetch()}
        hasMore={teamWindow.hasMore}
        loadingMore={teamWindow.loadingMore}
        onLoadMore={teamWindow.loadMore}
        isEmpty={users.length === 0}
        empty={{
          icon: icon,
          title: "No team members yet",
          description: isAdmin ? "Add HR or billing users to share hiring work." : undefined,
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="name" sortKey={sortKey} direction={direction} onSort={toggle}>
                Name
              </SortableTh>
              <SortableTh columnKey="email" sortKey={sortKey} direction={direction} onSort={toggle}>
                Email
              </SortableTh>
              <SortableTh columnKey="role" sortKey={sortKey} direction={direction} onSort={toggle}>
                Role
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {teamWindow.items.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">{row.user.full_name}</Table.Td>
                <Table.Td>{row.user.email}</Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="outline" color="gray">
                    {row.company_role.replace("_", " ")}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={row.is_active ? "active" : "inactive"} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title="Add team member" centered>
        <form onSubmit={form.onSubmit((values) => addMutation.mutate(values))}>
          <Stack>
            <TextInput label="Full name" required {...form.getInputProps("full_name")} />
            <TextInput label="Email" required {...form.getInputProps("email")} />
            <PhoneInput
              label="Phone (WhatsApp)"
              value={form.values.phone}
              onChange={(value) => form.setFieldValue("phone", value)}
              error={form.errors.phone}
            />
            <PasswordInput label="Password" required {...form.getInputProps("password")} />
            <Select
              label="Role"
              data={ROLE_OPTIONS}
              {...form.getInputProps("company_role")}
            />
            <Button type="submit" color="violet" loading={addMutation.isPending}>
              Create user
            </Button>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
